<script setup>
import { i18n } from 'boot/i18n';
import { HrActivitySpinner, HrIconActionBtn, HrTooltip } from 'src/design-system';
import { logEvent } from 'src/utils/LogEvent';
import { computed, watch } from 'vue';

import { VERDICT_OPTIONS, VERDICTS } from '../../domain/constants/auditResults';
import { CHECKOUT_AUDIT_STATUSES } from '../../domain/constants/checkoutAuditStatuses';
import { useCheckoutAuditor } from '../composables/useCheckoutAuditor';
import AuditVerdictBanner from './AuditVerdictBanner.vue';

defineOptions({ name: 'CheckoutAuditCard' });

const props = defineProps({
  sessionActivity: {
    type: Object,
    default: null,
  },
  sessionNoteTemplate: {
    type: Object,
    default: null,
  },
  notes: {
    type: String,
    default: '',
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  /** Contexts that must never audit — a template preview, or a local recovery copy on screen. */
  isSuspended: {
    type: Boolean,
    default: false,
  },
});

const {
  status,
  rationale,
  isEnabled,
  canRerun,
  start,
  rerun,
} = useCheckoutAuditor({
  getSessionActivity: () => props.sessionActivity,
  getSessionNoteTemplate: () => props.sessionNoteTemplate,
  getNotes: () => props.notes,
  getIsSuspended: () => props.isSuspended,
});

const verdictIcons = VERDICT_OPTIONS.reduce((icons, option) => ({
  ...icons,
  [option.value]: option.icon,
}), {});

// Verdict rows borrow the leader side's icons and labels; the rest explain an absent result.
const STATUS_PRESENTATION = {
  [CHECKOUT_AUDIT_STATUSES.RUNNING]: {
    tone: 'info',
    icon: 'hourglass_top',
    labelKey: 'noteAuditor.statusRunning',
    messageKey: 'noteAuditor.checkout.runningMessage',
  },
  [CHECKOUT_AUDIT_STATUSES.PASSED]: {
    tone: 'success',
    icon: verdictIcons[VERDICTS.PASS],
    labelKey: 'noteAuditor.feedback.verdictStatus.pass',
  },
  [CHECKOUT_AUDIT_STATUSES.FLAGGED]: {
    tone: 'warning',
    icon: verdictIcons[VERDICTS.FLAG],
    labelKey: 'noteAuditor.feedback.verdictStatus.flag',
  },
  [CHECKOUT_AUDIT_STATUSES.FAILED_VERDICT]: {
    tone: 'error',
    icon: verdictIcons[VERDICTS.FAIL],
    labelKey: 'noteAuditor.feedback.verdictStatus.fail',
  },
  [CHECKOUT_AUDIT_STATUSES.OUTDATED]: {
    tone: 'warning',
    icon: 'sync_problem',
    labelKey: 'noteAuditor.checkout.statusOutdated',
    messageKey: 'noteAuditor.checkout.outdatedMessage',
    rerunTooltipKey: 'noteAuditor.checkout.rerunTooltip',
    rerunAriaKey: 'noteAuditor.checkout.rerunAriaLabel',
  },
  [CHECKOUT_AUDIT_STATUSES.ERRORED]: {
    tone: 'warning',
    icon: 'running_with_errors',
    labelKey: 'noteAuditor.statusErrored',
    messageKey: 'noteAuditor.checkout.erroredMessage',
    rerunTooltipKey: 'noteAuditor.checkout.retryTooltip',
    rerunAriaKey: 'noteAuditor.checkout.retryAriaLabel',
  },
  [CHECKOUT_AUDIT_STATUSES.UNAVAILABLE]: {
    tone: 'warning',
    icon: 'cloud_off',
    labelKey: 'noteAuditor.statusUnavailable',
    messageKey: 'noteAuditor.checkout.unavailableMessage',
    rerunTooltipKey: 'noteAuditor.checkout.retryTooltip',
    rerunAriaKey: 'noteAuditor.checkout.retryAriaLabel',
  },
};

const presentation = computed(() => {
  const config = STATUS_PRESENTATION[status.value];

  if (!isEnabled.value || !config) {
    return null;
  }

  return {
    tone: config.tone,
    statusIcon: config.icon,
    statusLabel: i18n.t(config.labelKey),
    // A verdict's own wording is the auditor's rationale, which arrives with the written result.
    message: config.messageKey ? i18n.t(config.messageKey) : (rationale.value ?? ''),
  };
});

const rerunLabels = computed(() => STATUS_PRESENTATION[status.value] ?? {});

const isRunning = computed(() => status.value === CHECKOUT_AUDIT_STATUSES.RUNNING);

// The note renders asynchronously, so the surface can go active a tick before there is text.
const isReadyToAudit = computed(() => props.isActive && isEnabled.value && !!props.notes?.trim());

watch(isReadyToAudit, (isReady) => {
  if (isReady) {
    start().catch(logEvent);
  }
}, { immediate: true });

// Reached again when a marker this client waited on expires, leaving the note still unreviewed.
watch(status, (current) => {
  if (current === CHECKOUT_AUDIT_STATUSES.IDLE && isReadyToAudit.value) {
    start().catch(logEvent);
  }
});
</script>

<template>
  <audit-verdict-banner
    v-if="presentation"
    v-bind="presentation"
    data-e2e="card-checkout-audit"
  >
    <template #actions>
      <hr-activity-spinner
        v-if="isRunning"
        size="16px"
        :aria-label="i18n.t('noteAuditor.statusRunning')"
      />
      <hr-icon-action-btn
        v-else-if="canRerun"
        icon="refresh"
        data-e2e="button-rerun-audit"
        :aria-label="i18n.t(rerunLabels.rerunAriaKey)"
        @click="rerun"
      >
        <hr-tooltip>
          {{ i18n.t(rerunLabels.rerunTooltipKey) }}
        </hr-tooltip>
      </hr-icon-action-btn>
    </template>
  </audit-verdict-banner>
</template>
